import axios from 'axios';
import { API, CONTENT_TYPE, BACKEND_URL } from 'api/constants';
import type {
  Country,
  DeliveryMethod,
  DeploymentSupplier,
  ProductFamily,
  ResourceType,
} from 'types';
import { getSessionId, getVeevaDomain } from 'utils/sessionStorage';

export const apiInstance = axios.create({
  baseURL: BACKEND_URL,
});

export const getAuthorizationHeader = (sessionId: string = getSessionId()): { [key: string]: string } => {
  return {
    Authorization: sessionId,
  };
};

export const getVeevaDomainHeader = (veevaDomain: string = getVeevaDomain()): { [key: string]: string } => {
  return {
    'X-Veeva-Domain': veevaDomain,
  };
};

export const getCountriesFromVeeva = async (): Promise<{ data: Country[] }> => {
  const body = {
    contentType: CONTENT_TYPE.COUNTRY,
  };

  return apiInstance.post(
    API.GET_DATA_FROM_VEEVA,
    body,
    {
      headers: {
        ...getAuthorizationHeader(),
        ...getVeevaDomainHeader(),
      },
    },
  );
};

export const getProductFamiliesFromVeeva = async (): Promise<{ data: ProductFamily[] }> => {
  const body = {
    contentType: CONTENT_TYPE.PRODUCT_FAMILY,
  };

  return apiInstance.post(
    API.GET_DATA_FROM_VEEVA,
    body,
    {
      headers: {
        ...getAuthorizationHeader(),
        ...getVeevaDomainHeader(),
      },
    },
  );
};

export const getDeliveryMethodsFromVeeva = async (): Promise<{ data: DeliveryMethod[] }> => {
  const body = {
    contentType: CONTENT_TYPE.DELIVERY_METHOD,
  };

  return apiInstance.post(
    API.GET_DATA_FROM_VEEVA,
    body,
    {
      headers: {
        ...getAuthorizationHeader(),
        ...getVeevaDomainHeader(),
      },
    },
  );
};

export const getDeploymentSuppliersFromVeeva = async (): Promise<{ data: DeploymentSupplier[] }> => {
  const body = {
    contentType: CONTENT_TYPE.DEPLOYMENT_SUPPLIER,
  };

  return apiInstance.post(
    API.GET_DATA_FROM_VEEVA,
    body,
    {
      headers: {
        ...getAuthorizationHeader(),
        ...getVeevaDomainHeader(),
      },
    },
  );
};

export const getResourceTypeByCountryFromVeeva = async (
  countryId: string,
): Promise<{ data: ResourceType[] }> => {
  const body = {
    contentType: CONTENT_TYPE.RESOURCE_TYPE_BY_COUNTRY,
    id: countryId,
  };

  return apiInstance.post(
    API.GET_DATA_FROM_VEEVA,
    body,
    {
      headers: {
        ...getAuthorizationHeader(),
        ...getVeevaDomainHeader(),
      },
    },
  );
};

export async function getRendition(
  documentId: string,
  documentVersionId?: string,
): Promise<string> {
  const mode = document.location.pathname.startsWith('/simulation') ? 'simulator' : 'selector';

  const response = await fetch(
    `${BACKEND_URL}${API.GET_RENDITION}/${documentVersionId ?? documentId}?mode=${mode}`,
    {
      cache: 'default',
      method: 'get',
      headers: {
        ...getAuthorizationHeader(),
        ...getVeevaDomainHeader(),
      },
    },
  );

  if (!response.ok) {
    return '';
  }

  return URL.createObjectURL(await response.blob());
}
